import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, ShoppingBag } from 'lucide-react';
import PageShell from '../components/PageShell';
import { AnimatePresence, motion } from 'framer-motion';
import BoardForm from './garage/BoardForm';
import BoardCard from './garage/BoardCard';
import TransferModal from './garage/TransferModal';
import StatusModal from './garage/StatusModal';
import DeleteConfirmModal from './garage/DeleteConfirmModal';
import SuitsSection from './garage/SuitsSection';
import LostBoardsSection from './garage/LostBoardsSection';
import QuiverBuilder from './garage/QuiverBuilder';
import { authFetch, API } from '../utils/api';


export default function Garage() {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showHistory, setShowHistory] = useState(null);
  const [transferModal, setTransferModal] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [filter, setFilter] = useState('all');

  // Brand + options state
  const [brandNames, setBrandNames] = useState([]);
  const [lengthOpts, setLengthOpts] = useState([]);
  const [volumeOpts, setVolumeOpts] = useState([]);
  const [finOpts, setFinOpts] = useState([]);

  const [prefillType, setPrefillType] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/boards`);
      if (res.ok) setBoards((await res.json()).boards || []);
    } catch {} finally { setLoading(false); }
  }, []);

  const loadOptions = useCallback(async () => {
    try {
      const [brandsRes, optsRes] = await Promise.all([
        authFetch(`${API}/api/boards/brands`),
        authFetch(`${API}/api/boards/options`),
      ]);
      if (brandsRes.ok) {
        const data = await brandsRes.json();
        setBrandNames((data.brands || []).map(b => b.name));
      }
      if (optsRes.ok) {
        const data = await optsRes.json();
        setLengthOpts(data.lengths || []);
        setVolumeOpts(data.volumes || []);
        setFinOpts(data.fins || []);
      }
    } catch {}
  }, []);

  useEffect(() => { load(); loadOptions(); }, [load, loadOptions]);

  const handleAddBrand = async (name) => {
    try {
      const res = await authFetch(`${API}/api/boards/brands`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ name }),
      });
      if (res.ok) {
        setBrandNames(prev => [...prev, name].sort((a, b) => a.localeCompare(b)));
      }
    } catch {}
  };

  const handleAdd = async (form) => {
    try {
      const res = await authFetch(`${API}/api/boards`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify(form),
      });
      if (res.ok) { load(); setShowAdd(false); }
    } catch {}
  };

  const handleUpdate = async (boardId, form) => {
    try {
      await authFetch(`${API}/api/boards/${boardId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify(form),
      });
      load(); setEditingId(null);
    } catch {}
  };

  const handleDelete = async (boardId, confirmName) => {
    try {
      const res = await authFetch(`${API}/api/boards/${boardId}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ confirm_name: confirmName }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: data.detail || 'Delete failed' };
      }
      load();
      return {};
    } catch { return { error: 'Network error' }; }
  };

  const handleTransfer = async (boardId, type, toUsername, notes) => {
    try {
      const res = await authFetch(`${API}/api/boards/${boardId}/transfer`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ type, to_username: toUsername, notes }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: data.detail || 'Transfer failed' };
      }
      load();
      return {};
    } catch { return { error: 'Network error' }; }
  };

  const handleReturn = async (boardId) => {
    try {
      await authFetch(`${API}/api/boards/${boardId}/return`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify({ notes: '' }),
      });
      load();
    } catch {}
  };

  const handleStatusChange = async (boardId, status, notes, lostDate, lostSpot) => {
    try {
      const body = { status, notes };
      if (lostDate) body.lost_date = lostDate;
      if (lostSpot) body.lost_spot = lostSpot;
      await authFetch(`${API}/api/boards/${boardId}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },         body: JSON.stringify(body),
      });
      load();
    } catch {}
  };

  // Filter logic — new categories: active, lent, lost, archive
  const filteredBoards = boards.filter(b => {
    const s = b.status || 'active';
    if (filter === 'active') return s === 'active';
    if (filter === 'lent') return s === 'lent';
    if (filter === 'lost') return s === 'lost';
    if (filter === 'archive') return ['sold', 'recycled', 'deleted', 'gifted', 'found'].includes(s);
    return true; // 'all'
  });

  const activeCount = boards.filter(b => !b.status || b.status === 'active').length;
  const lentCount = boards.filter(b => b.status === 'lent').length;
  const lostCount = boards.filter(b => b.status === 'lost').length;
  const archiveCount = boards.filter(b => b.status && ['sold', 'recycled', 'deleted', 'gifted', 'found'].includes(b.status)).length;

  if (loading) {
    return (
      <PageShell title="Garage">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Garage">
      <div className="p-5" data-testid="garage-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-muted-foreground">{boards.length} board{boards.length !== 1 ? 's' : ''} in your quiver</p>
          </div>
          <button
            data-testid="garage-add-btn"
            onClick={() => { setShowAdd(!showAdd); setEditingId(null); }}
            className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-4 py-1.5 rounded-full inline-flex items-center gap-1 transition-colors"
          >
            {showAdd ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {showAdd ? 'Cancel' : 'Add board'}
          </button>
        </div>

        {/* Filter tabs */}
        {boards.length > 0 && (
          <div className="flex gap-1.5 mb-4 flex-wrap" data-testid="garage-filters">
            {[
              { key: 'all', label: `All (${boards.length})` },
              { key: 'active', label: `Active (${activeCount})` },
              { key: 'lent', label: `Lent (${lentCount})` },
              { key: 'lost', label: `Lost (${lostCount})` },
              { key: 'archive', label: `Archive (${archiveCount})` },
            ].map(f => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`text-[10px] px-3 py-1.5 rounded-full transition-all ${
                  filter === f.key
                    ? f.key === 'lost'
                      ? 'bg-red-500/15 text-red-400 border border-red-500/30'
                      : f.key === 'lent'
                        ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/30'
                        : f.key === 'archive'
                          ? 'bg-gray-500/15 text-gray-400 border border-gray-500/30'
                          : 'bg-primary/15 text-primary border border-primary/30'
                    : 'bg-white/5 text-muted-foreground border border-white/5 hover:border-white/10'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Add Form */}
        <AnimatePresence>
          {showAdd && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mb-4">
              <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-4">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">New Board</p>
                <BoardForm initial={prefillType ? { name: '', board_type: prefillType, length: '', volume: '', fins: '', brand: '', condition: 'Good', notes: '' } : undefined} onSave={(form) => { handleAdd(form); setPrefillType(''); }} onCancel={() => { setShowAdd(false); setPrefillType(''); }} brands={brandNames} lengths={lengthOpts} volumes={volumeOpts} fins={finOpts} onAddBrand={handleAddBrand} />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quiver Builder */}
        <div className="mb-4">
          <QuiverBuilder onAddBoard={(type) => {
            setPrefillType(type);
            setShowAdd(true);
            setEditingId(null);
          }} />
        </div>

        {/* Suits Section */}
        <div className="mb-4">
          <SuitsSection />
        </div>

        {/* Community Lost & Found */}
        <div className="mb-4">
          <LostBoardsSection />
        </div>

        {/* Marketplace teaser */}
        <div className="bg-secondary/5 border border-secondary/15 rounded-2xl p-3 mb-4 flex items-center gap-3">
          <ShoppingBag className="w-4 h-4 text-secondary flex-shrink-0" />
          <div className="flex-1">
            <p className="text-[11px] text-muted-foreground leading-snug">
              <span className="text-secondary font-medium">Marketplace</span> — buy & sell boards with the community. Coming soon.
            </p>
          </div>
          <span className="text-[10px] text-secondary/60 bg-secondary/10 px-2 py-0.5 rounded-lg flex-shrink-0">Soon</span>
        </div>

        {/* Board List */}
        {filteredBoards.length > 0 ? (
          <div className="space-y-3">
            {filteredBoards.map((board, i) => (
              <BoardCard
                key={board.board_id}
                board={board}
                index={i}
                isExpanded={expandedId === board.board_id}
                isEditing={editingId === board.board_id}
                isHistoryOpen={showHistory === board.board_id}
                onToggleExpand={() => { setExpandedId(expandedId === board.board_id ? null : board.board_id); setEditingId(null); setShowHistory(null); }}
                onEdit={(id) => setEditingId(id)}
                onCancelEdit={() => setEditingId(null)}
                onDelete={(id) => {
                  const b = boards.find(x => x.board_id === id);
                  if (b) setDeleteModal(b);
                }}
                onUpdate={handleUpdate}
                onReturn={handleReturn}
                onTransfer={(b, type) => setTransferModal({ board: b, type })}
                onStatusChange={(b, status) => setStatusModal({ board: b, status })}
                onToggleHistory={() => setShowHistory(showHistory === board.board_id ? null : board.board_id)}
                onPhotoUploaded={load}
                brandNames={brandNames}
                lengthOpts={lengthOpts}
                volumeOpts={volumeOpts}
                finOpts={finOpts}
                onAddBrand={handleAddBrand}
              />
            ))}
          </div>
        ) : !showAdd ? (
          <div className="text-center py-12">
            <div className="text-3xl mb-3">🏄</div>
            {filter !== 'all' ? (
              <>
                <p className="text-sm text-muted-foreground">no boards match this filter</p>
                <button onClick={() => setFilter('all')} className="text-xs text-primary mt-2 hover:underline">show all</button>
              </>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">your garage is empty</p>
                <p className="text-[10px] text-muted-foreground/60 mt-1 mb-4">add your boards to track which ones you ride</p>
                <button onClick={() => setShowAdd(true)} className="text-xs bg-primary text-black font-bold px-5 py-2 rounded-xl hover:opacity-90 transition-opacity">
                  Add your first board
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* Modals */}
      <AnimatePresence>
        {transferModal && (
          <TransferModal
            board={transferModal.board}
            type={transferModal.type}
            onClose={() => setTransferModal(null)}
            onConfirm={handleTransfer}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {statusModal && (
          <StatusModal
            board={statusModal.board}
            status={statusModal.status}
            onClose={() => setStatusModal(null)}
            onConfirm={handleStatusChange}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {deleteModal && (
          <DeleteConfirmModal
            board={deleteModal}
            onClose={() => setDeleteModal(null)}
            onConfirm={handleDelete}
          />
        )}
      </AnimatePresence>
    </PageShell>
  );
}
